import pick from "lodash.pick";

type JsonSchema = Record<string, any>;

/**
 * Picks properties from an existing JSON schema and updates the `required` keyword
 * to reflect the new set of required properties.
 *
 * @param existingSchema - The existing JSON schema to pick properties from.
 * @param keysToPick - An array of keys to pick from the existing schema.
 * @returns A new JSON schema with the picked properties and updated `required` keyword.
 */
export function pickJsonSchemaProps(
  existingSchema: JsonSchema,
  keysToPick: string[]
): JsonSchema {
  const pickedSchema: JsonSchema = {
    type: "object",
    properties: pick(existingSchema.properties, keysToPick),
    required: existingSchema.required
      ? existingSchema.required.filter(key => keysToPick.includes(key))
      : undefined
  };

  // getting definitions 
  const pickedDefinitionNames = new Set<string>();
  for (const key of keysToPick) {
    let schema = existingSchema;
    const segments = key.split(".");
    for (const segment of segments) {
      schema = schema.properties && schema.properties[segment];
    }
    if (schema && schema.$ref) {
      const refSegments = schema.$ref.split("/");
      const definitionName = refSegments[refSegments.length - 1];
      pickedDefinitionNames.add(definitionName);
    }
  }
  const pickedDefinitions: { [name: string]: JsonSchema } = {};
  for (const name of pickedDefinitionNames) {
    const definition = existingSchema.definitions && existingSchema.definitions[name];
    if (definition) {
      pickedDefinitions[name] = definition;
    }
  }

  pickedSchema.definitions = pickedDefinitions;

  // Recursively update the required properties of any nested objects
  for (const key in pickedSchema.properties) {
    const property = pickedSchema.properties[key];
    if (property.type === "object") {
      property.required = property.required
        ? property.required.filter(subKey => property.properties[subKey])
        : undefined;
    }
  }

  return pickedSchema;
}
